import { Component, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { DataServiceService } from '../../services/data-service.service';
import { Orders, TrailerLocation } from '../../interfaces';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DateTime } from 'luxon';
import { ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';


@Component({
  selector: 'app-drag-and-drop',
  templateUrl: './drag-and-drop.component.html',
  styleUrl: './drag-and-drop.component.scss',
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({
        height: '0',
        opacity: '0'
      })),
      state('expanded', style({
        height: '*',
        opacity: '1'
      })),
      transition('collapsed <=> expanded', [
        animate('200ms ease-in-out')
      ])
    ])
  ]
})


export class DragAndDropComponent implements OnInit, OnDestroy {
  constructor(private dataServiceService: DataServiceService, private cdr: ChangeDetectorRef, private authService: AuthService) { }


  @Input() driverCards: any[] = [];
  @Output() orderAssigned = new EventEmitter<any>();
  @Output() orderUnassigned = new EventEmitter<void>();

  allOrders: Orders[] = [];      // all orders fetched from db
  sidebarVisible: boolean = false;
  groupedOrders: { [key: string]: Orders[] } = {};  // orders grouped by type
  draggedOrder: any = null;     // order selected to be assigned
  availableOrders: Orders[] = [];   // all orders in dispatch mode that are unassigned
  noMoreLoads: boolean = false;   // indicates if there are any more loads of a certain type
  selectedLoadTypes: { [key: string]: boolean } = {};
  Object = Object;
  selectedDate: Date = new Date();
  expanded = false; // filter options 
  visibleCount: number = 3; // Number of filter option items to show in single row
  customers: TrailerLocation[] = [];
  hasAccess = false;

  // Indicators for orders on adjacent dates
  hasOrdersOnPreviousDay: boolean = false;
  hasOrdersOnNextDay: boolean = false;

  private subscription: Subscription = new Subscription(); // Initialize subscription to avoid lint error

  showUnscheduled: boolean = false;  // Add this property

  // Add this property to your component class
  unscheduledOrders: Orders[] = [];
  groupedUnscheduledOrders: { [key: string]: Orders[] } = {};

  showScheduled: boolean = true;  // Default to expanded

  // Track collapsed customer groups
  collapsedGroups: Set<string> = new Set<string>();

  ngOnInit() {
    // Subscribe to real-time order updates
    this.subscription = this.dataServiceService.orders$.subscribe({
      next: (data) => {
        this.allOrders = [...data];
        this.filterOrdersByDate();
        this.checkAdjacentDays();
      },
      error: (error) => {
        console.error('Error in orders subscription:', error);
      }
    });

    // Initial load of orders
    this.dataServiceService.getOrders().subscribe({
      error: (error) => console.error('Error loading initial orders:', error)
    });

    // Load customers from shared cache
    this.dataServiceService.customers$.subscribe({
      next: (customers: any[]) => this.customers = customers,
      error: (error: any) => console.error('Error loading customers:', error)
    });
    this.authService.userProfile$.subscribe((profile) => {
      if (profile?.roles?.includes('Dispatcher') || profile?.roles?.includes('Admin')) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  // Format date to YYYY-MM-DD for comparison
  private formatDate(date: Date): string {
    return DateTime.fromJSDate(date)
      .setZone('America/Chicago')
      .toFormat('yyyy-MM-dd');
  }

  // Filter orders based on the selected date
  filterOrdersByDate() {
    const selectedDateStr = this.formatDate(this.selectedDate);

    // Filter unscheduled orders
    const unsortedUnscheduledOrders = this.allOrders.filter(order => 
        order.load_status === 'Unassigned' &&
        !order.pickup_driver_id &&
        !order.delivery_driver_id &&
        (!order.delivery_date)
    );

    // Group by customer and sort within each group by pickup number
    this.groupedUnscheduledOrders = this.groupUnscheduledOrdersByCustomer(unsortedUnscheduledOrders);
    this.unscheduledOrders = Object.values(this.groupedUnscheduledOrders)
      .flat()
      .sort((a, b) => {
        if (!a.pickup_number) return 1;
        if (!b.pickup_number) return -1;
        return a.pickup_number.localeCompare(b.pickup_number, undefined, {numeric: true});
      });

    // Filter for orders with dates (existing logic)
    this.availableOrders = this.allOrders.filter(order => {
      const orderDeliveryDate = this.formatDate(new Date(order.delivery_date));
      return order.load_status === 'Unassigned' &&
        !order.pickup_driver_id &&
        !order.delivery_driver_id &&
        orderDeliveryDate === selectedDateStr;
    });

    // Update grouped orders
    this.groupedOrders = this.groupOrdersByLoadType(this.availableOrders);
    this.noMoreLoads = this.availableOrders.length === 0;

    this.cdr.detectChanges();
  }

  // Check if there are orders on adjacent days
  checkAdjacentDays() {
    // Check previous day
    const prevDate = new Date(this.selectedDate);
    prevDate.setDate(prevDate.getDate() - 1);
    const prevDateStr = this.formatDate(prevDate);

    this.hasOrdersOnPreviousDay = this.allOrders.some(order => {
      const orderDeliveryDate = this.formatDate(new Date(order.delivery_date));
      return order.load_status === 'Unassigned' &&
        !order.pickup_driver_id &&
        !order.delivery_driver_id &&
        orderDeliveryDate === prevDateStr;
    });

    // Check next day
    const nextDate = new Date(this.selectedDate);
    nextDate.setDate(nextDate.getDate() + 1);
    const nextDateStr = this.formatDate(nextDate);

    this.hasOrdersOnNextDay = this.allOrders.some(order => {
      const orderDeliveryDate = this.formatDate(new Date(order.delivery_date));
      return order.load_status === 'Unassigned' &&
        !order.pickup_driver_id &&
        !order.delivery_driver_id &&
        orderDeliveryDate === nextDateStr;
    });

    this.cdr.detectChanges();
  }

  // Method to remove an order when it's assigned
  removeAssignedOrder(orderId: number) {
    // Remove from available orders
    this.availableOrders = this.availableOrders.filter(order => order.order_id !== orderId);
    // Regroup the remaining orders
    this.groupedOrders = this.groupOrdersByLoadType(this.availableOrders);
    this.noMoreLoads = this.availableOrders.length === 0;

    // Re-check adjacent days as this might affect the indicators
    this.checkAdjacentDays();
  }


  getFilteredGroupedOrders(): { [key: string]: Orders[] } {
    const isAnyLoadTypeSelected = Object.values(this.selectedLoadTypes).some(value => value);

    // If no load types are selected, return all unassigned orders
    if (!isAnyLoadTypeSelected) {
      return this.groupedOrders;
    }

    // Filter by selected load types
    const filtered = Object.keys(this.groupedOrders)
      .filter(loadType => this.selectedLoadTypes[loadType])
      .reduce((filtered: { [key: string]: Orders[] }, loadType) => {
        filtered[loadType] = this.groupedOrders[loadType];
        return filtered;
      }, {});

    return filtered;
  }

  toggleExpand() {
    this.expanded = !this.expanded;
  }


  // Group orders by load type
  private groupOrdersByLoadType(orders: Orders[]): { [key: string]: Orders[] } {

    return orders.reduce((groups, order) => {
      // Ensure load_type exists and handle it as a string
      const loadType = order.load_type || 'Unassigned';

      // Initialize the array for this load type if it doesn't exist
      if (!groups[loadType]) {
        groups[loadType] = [];
      }

      // Add the order to its load type group
      groups[loadType].push(order);

      return groups;
    }, {} as { [key: string]: Orders[] });
  }

  // Starts the drag from the sidebar
  onDragStart(event: any, order: Orders) {
    if (this.hasAccess) {
      this.draggedOrder = order;
      this.orderAssigned.emit(order);
    }
  }

  onDragEnd() {
    this.draggedOrder = null;
  }

  onDropToUnassign() {
    this.orderUnassigned.emit();
  }

  // Gets the tooltip for the split load icon
  getSplitLoadTooltip(order: any): string {
    const pickupDriver = this.driverCards.find(d => d.id === order.pickupDriver);
    const dropOffDriver = this.driverCards.find(d => d.id === order.dropOffDriver);

    if (order.pickupDriver === 0) {
      return `Split Load:
  Needs Pickup Driver
  Drop-off assigned to: ${dropOffDriver?.name || 'Unknown'}`;
    } else if (order.dropOffDriver === 0) {
      return `Split Load:
  Needs Drop-off Driver
  Pickup assigned to: ${pickupDriver?.name || 'Unknown'}`;
    }
    return `Split Load:
  Pickup: ${pickupDriver?.name || 'Unknown'}
  Drop-off: ${dropOffDriver?.name || 'Unknown'}`;
  }


  previousDay() {
    const date = new Date(this.selectedDate);
    date.setDate(date.getDate() - 1);
    this.selectedDate = date;
    this.filterOrdersByDate();
    this.checkAdjacentDays();
  }

  nextDay() {
    const date = new Date(this.selectedDate);
    date.setDate(date.getDate() + 1);
    this.selectedDate = date;
    this.filterOrdersByDate();
    this.checkAdjacentDays();
  }

  // Handle date selection from calendar
  onDateSelect() {
    this.filterOrdersByDate();
    this.checkAdjacentDays();
  }


  toCentralTime(date: string | Date): string {
    const dt = DateTime.fromISO(date.toString(), { zone: 'utc' });
    const centralTime = dt.setZone('America/Chicago');
    // Check if the time is midnight in Central Time (meaning no specific time was selected)
    if (centralTime.hour === 0 && centralTime.minute === 0 && centralTime.second === 0) {
      return centralTime.toFormat('MM/dd/yyyy');
    }
    return centralTime.toFormat('MM/dd/yyyy h:mm a');
}


  getCustomerName(locationId: number): string {
    const customer = this.customers.find(c => c.customer_id === locationId);
    return customer ? customer.name || 'Unknown' : 'Unknown';
  }

  toggleUnscheduled() {
    this.showUnscheduled = !this.showUnscheduled;
  }

  toggleScheduled() {
    this.showScheduled = !this.showScheduled;
  }

  // Add this method to group unscheduled orders by customer
  public groupUnscheduledOrdersByCustomer(orders: Orders[]): { [key: string]: Orders[] } {
    const grouped = orders.reduce((groups, order) => {
      // Use a stable key (supplier_id) so collapse state persists.
      const customerKey = String(order.supplier_id ?? 'unknown');
      if (!groups[customerKey]) {
        groups[customerKey] = [];
      }
      groups[customerKey].push(order);
      return groups;
    }, {} as { [key: string]: Orders[] });

    // Sort orders within each group by pickup number
    Object.keys(grouped).forEach(customerKey => {
      grouped[customerKey].sort((a, b) => {
        if (!a.pickup_number) return 1;
        if (!b.pickup_number) return -1;
        return a.pickup_number.localeCompare(b.pickup_number, undefined, {numeric: true});
      });
    });

    return grouped;
  }

  // Toggle specific customer group
  toggleUnscheduledGroup(groupKey: string | number) {
    const normalizedKey = String(groupKey);
    if (this.collapsedGroups.has(normalizedKey)) {
      this.collapsedGroups.delete(normalizedKey);
    } else {
      this.collapsedGroups.add(normalizedKey);
    }
  }

  // Check if a specific group is collapsed
  isGroupCollapsed(groupKey: string | number): boolean {
    return this.collapsedGroups.has(String(groupKey));
  }

  getUnscheduledGroupLabel(groupKey: string): string {
    if (groupKey === 'unknown') {
      return 'Unknown';
    }
    return this.getCustomerName(Number(groupKey));
  }
}
